import React, { useState, createElement, Component } from 'react';
import { Box, Typography, Tabs, Tab, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Card, CardContent, Grid, LinearProgress, Select, MenuItem, FormControl, InputLabel, Accordion, AccordionSummary, AccordionDetails, Menu, TextField, Chip } from '@mui/material';
import { Download as DownloadIcon, ChevronDown as ChevronDownIcon, Filter as FilterIcon, FileText as FileTextIcon, FileSpreadsheet as FileSpreadsheetIcon, File as FileIcon, Search as SearchIcon } from 'lucide-react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
// --- Utility ---
const downloadCSV = (data: any[], filename: string) => {
  if (!data || !data.length) return;
  const headers = Object.keys(data[0]);
  const csvRows: string[] = [];
  csvRows.push(headers.join(','));
  for (const row of data) {
    const values = headers.map(header => {
      const val = row[header] === null || row[header] === undefined ? '' : row[header];
      const escaped = ('' + val).replace(/"/g, '""');
      return `"${escaped}"`;
    });
    csvRows.push(values.join(','));
  }
  const blob = new Blob([csvRows.join('\n')], {
    type: 'text/csv'
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.setAttribute('hidden', '');
  a.setAttribute('href', url);
  a.setAttribute('download', filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};
// --- Export Menu Component ---
const ExportMenu = ({
  onExport,
  filename
}: {
  onExport: (type: string) => void;
  filename: string;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (type?: string) => {
    setAnchorEl(null);
    if (type) {
      onExport(type);
    }
  };
  return <>
      <Button variant="outlined" startIcon={<DownloadIcon size={16} data-id="element-2106" />} endIcon={<ChevronDownIcon size={16} data-id="element-2107" />} onClick={handleClick} sx={{
      borderColor: '#E5E7EB',
      color: '#1F2937',
      textTransform: 'none',
      fontWeight: 600,
      '&:hover': {
        borderColor: '#D1D5DB',
        backgroundColor: '#F9FAFB'
      }
    }} data-id="element-2105">
        Export
      </Button>
      <Menu anchorEl={anchorEl} open={open} onClose={() => handleClose()} PaperProps={{
      elevation: 0,
      sx: {
        overflow: 'visible',
        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.1))',
        mt: 1,
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        minWidth: 180
      }
    }} data-id="element-2108">
        <MenuItem onClick={() => handleClose('csv')} sx={{
        fontSize: '0.875rem',
        gap: 1.5
      }} data-id="element-2109">
          <FileTextIcon size={16} color="#6B7280" data-id="element-2110" /> Export as CSV
        </MenuItem>
        <MenuItem onClick={() => handleClose('excel')} sx={{
        fontSize: '0.875rem',
        gap: 1.5
      }} data-id="element-2111">
          <FileSpreadsheetIcon size={16} color="#10B981" data-id="element-2112" /> Export as Excel
        </MenuItem>
        <MenuItem onClick={() => handleClose('pdf')} sx={{
        fontSize: '0.875rem',
        gap: 1.5
      }} data-id="element-2113">
          <FileIcon size={16} color="#EF4444" data-id="element-2114" /> Export as PDF
        </MenuItem>
      </Menu>
    </>;
};
// --- Tab Panel ---
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel(props: TabPanelProps) {
  const {
    children,
    value,
    index,
    ...other
  } = props;
  return <div role="tabpanel" hidden={value !== index} id={`reports-tabpanel-${index}`} aria-labelledby={`reports-tab-${index}`} {...other} style={{
    height: '100%'
  }} data-id="element-2115">
      {value === index && <Box sx={{
      py: 3,
      height: '100%'
    }} data-id="element-2116">
          {children}
        </Box>}
    </div>;
}
// --- Mock Data Generators ---
const benchmarks = {
  preScreening: 2.0,
  titleFetch: 5.0,
  partnerBalance: 4.0,
  fundsCredited: 3.0,
  totalTat: 14.0
};
const dailyMonitoringData = Array.from({
  length: 15
}).map((_, i) => {
  const date = new Date();
  date.setDate(date.getDate() - i);
  const transactions = Math.floor(Math.random() * 5000 + 10000);
  const preScreening = parseFloat((Math.random() * 3 + 1).toFixed(1));
  const titleFetch = parseFloat((Math.random() * 6 + 2).toFixed(1));
  const partnerBalance = parseFloat((Math.random() * 5 + 1).toFixed(1));
  const fundsCredited = parseFloat((Math.random() * 4 + 1).toFixed(1));
  const totalTat = parseFloat((preScreening + titleFetch + partnerBalance + fundsCredited).toFixed(1));
  const failedCount = Math.floor(transactions * (Math.random() * 0.03 + 0.005));
  const avgTxnValue = 28000 + Math.random() * 4000;
  const failedValue = parseFloat((failedCount * avgTxnValue / 1000000).toFixed(1));
  return {
    date: date.toISOString().split('T')[0],
    transactions,
    failedCount,
    failedValue,
    preScreening,
    titleFetch,
    partnerBalance,
    fundsCredited,
    totalTat
  };
});
// --- Tab 1: Daily Monitoring ---
const DailyMonitoringTab = () => {
  const handleExport = (type: string) => {
    const csvData = dailyMonitoringData.map(r => ({
      Date: r.date,
      'No. of Transactions': r.transactions,
      'Failed Txn Count': r.failedCount,
      'Failed Txn Value (PKR M)': r.failedValue,
      'Pre Screening (TAT)': r.preScreening,
      'Title Fetch (TAT)': r.titleFetch,
      'Partner Balance (TAT)': r.partnerBalance,
      'Funds Credited': r.fundsCredited,
      'Total TAT': r.totalTat
    }));
    downloadCSV(csvData, `daily_monitoring.${type === 'excel' ? 'xlsx' : type}`);
  };
  const getTatColor = (val: number, benchmark: number) => val <= benchmark ? '#0B7923' : '#AE2A19';
  return <Box data-id="element-2117">
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2
    }} data-id="element-2118">
        <Typography variant="h6" sx={{
        fontWeight: 600,
        color: '#1F2937'
      }} data-id="element-2119">
          Daily Monitoring (TAT)
        </Typography>
        <ExportMenu onExport={handleExport} filename="daily_monitoring" data-id="element-2120" />
      </Box>
      <TableContainer component={Paper} elevation={0} sx={{
      border: '1px solid #E5E7EB',
      borderRadius: '8px'
    }} data-id="element-2121">
        <Table size="small" data-id="element-2122">
          <TableHead sx={{
          bgcolor: '#F8F9FA'
        }} data-id="element-2123">
            <TableRow data-id="element-2124">
              <TableCell sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2125">
                Date
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2126">
                Transactions
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#AE2A19'
            }} data-id="element-2127">
                Failed Count
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#AE2A19'
            }} data-id="element-2128">
                Failed Value (M)
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2129">
                Pre Screen
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2130">
                Title Fetch
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2131">
                Partner Bal
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2132">
                Funds Credited
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#1F2937'
            }} data-id="element-2133">
                Total TAT
              </TableCell>
            </TableRow>
            <TableRow sx={{
            bgcolor: '#F0F3F7'
          }} data-id="element-2134">
              <TableCell colSpan={4} sx={{
              fontWeight: 600,
              color: '#6B7280',
              fontSize: '0.75rem'
            }} data-id="element-2135">
                BENCHMARK (MINUTES)
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2136">
                2.0
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2137">
                5.0
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2138">
                4.0
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2139">
                3.0
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#1F2937'
            }} data-id="element-2140">
                14.0
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-id="element-2141">
            {dailyMonitoringData.map(row => <TableRow key={row.date} hover data-id="element-2142">
                <TableCell sx={{
              fontWeight: 500
            }} data-id="element-2143">
                  {row.date}
                </TableCell>
                <TableCell align="right" data-id="element-2144">
                  {row.transactions.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{
              color: '#AE2A19',
              fontWeight: 500
            }} data-id="element-2145">
                  {row.failedCount.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{
              color: '#AE2A19',
              fontWeight: 500
            }} data-id="element-2146">
                  {row.failedValue}
                </TableCell>
                <TableCell align="right" sx={{
              color: getTatColor(row.preScreening, benchmarks.preScreening),
              fontWeight: 500
            }} data-id="element-2147">
                  {row.preScreening}m
                </TableCell>
                <TableCell align="right" sx={{
              color: getTatColor(row.titleFetch, benchmarks.titleFetch),
              fontWeight: 500
            }} data-id="element-2148">
                  {row.titleFetch}m
                </TableCell>
                <TableCell align="right" sx={{
              color: getTatColor(row.partnerBalance, benchmarks.partnerBalance),
              fontWeight: 500
            }} data-id="element-2149">
                  {row.partnerBalance}m
                </TableCell>
                <TableCell align="right" sx={{
              color: getTatColor(row.fundsCredited, benchmarks.fundsCredited),
              fontWeight: 500
            }} data-id="element-2150">
                  {row.fundsCredited}m
                </TableCell>
                <TableCell align="right" sx={{
              color: getTatColor(row.totalTat, benchmarks.totalTat),
              fontWeight: 600
            }} data-id="element-2151">
                  {row.totalTat}m
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>;
};
// --- Tab 2: Financial Flow ---
const FinancialFlowTab = () => {
  const handleExport = (type: string) => {
    const data = [{
      Metric: 'Total YTD Volume (USD)',
      Value: '$450,230,000'
    }, {
      Metric: 'Target Achievement %',
      Value: '64.3%'
    }, {
      Metric: 'Average Daily Flow (YTD)',
      Value: '$3,215,000'
    }, {
      Metric: 'Current Month Forecast',
      Value: '$52,400,000'
    }];
    downloadCSV(data, `financial_flow.${type === 'excel' ? 'xlsx' : type}`);
  };
  return <Box data-id="element-2152">
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 3
    }} data-id="element-2153">
        <Typography variant="h6" sx={{
        fontWeight: 600,
        color: '#1F2937'
      }} data-id="element-2154">
          Financial Flow Monitor
        </Typography>
        <ExportMenu onExport={handleExport} filename="financial_flow" data-id="element-2155" />
      </Box>
      <Grid container spacing={3} data-id="element-2156">
        <Grid item xs={12} md={6} data-id="element-2157">
          <Card elevation={0} sx={{
          border: '1px solid #E5E7EB',
          height: '100%',
          borderRadius: '8px'
        }} data-id="element-2158">
            <CardContent data-id="element-2159">
              <Typography variant="subtitle2" color="#6B7280" gutterBottom fontWeight={600} textTransform="uppercase" data-id="element-2160">
                Total YTD Volume (USD)
              </Typography>
              <Typography variant="h3" sx={{
              fontWeight: 700,
              color: '#1F2937',
              mb: 1
            }} data-id="element-2161">
                $450.2M
              </Typography>
              <Typography variant="body2" sx={{
              fontWeight: 600,
              color: '#0B7923'
            }} data-id="element-2162">
                ↑ +12.5% vs last year
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} data-id="element-2163">
          <Card elevation={0} sx={{
          border: '1px solid #E5E7EB',
          height: '100%',
          borderRadius: '8px'
        }} data-id="element-2164">
            <CardContent data-id="element-2165">
              <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1
            }} data-id="element-2166">
                <Typography variant="subtitle2" color="#6B7280" fontWeight={600} textTransform="uppercase" data-id="element-2167">
                  Target Achievement ($700M Goal)
                </Typography>
                <Typography variant="subtitle2" sx={{
                fontWeight: 700,
                color: '#1F2937'
              }} data-id="element-2168">
                  64.3%
                </Typography>
              </Box>
              <Typography variant="h3" sx={{
              fontWeight: 700,
              color: '#1F2937',
              mb: 2
            }} data-id="element-2169">
                $450.2M
              </Typography>
              <LinearProgress variant="determinate" value={64.3} sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: '#E5E7EB',
              '& .MuiLinearProgress-bar': {
                bgcolor: '#0052CC'
              }
            }} data-id="element-2170" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} data-id="element-2171">
          <Card elevation={0} sx={{
          border: '1px solid #E5E7EB',
          height: '100%',
          borderRadius: '8px'
        }} data-id="element-2172">
            <CardContent data-id="element-2173">
              <Typography variant="subtitle2" color="#6B7280" gutterBottom fontWeight={600} textTransform="uppercase" data-id="element-2174">
                Average Daily Flow (YTD)
              </Typography>
              <Typography variant="h4" sx={{
              fontWeight: 700,
              color: '#1F2937',
              mb: 1
            }} data-id="element-2175">
                $3.21M
              </Typography>
              <Typography variant="body2" sx={{
              fontWeight: 600,
              color: '#0B7923'
            }} data-id="element-2176">
                ↑ +5.2% vs last month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} data-id="element-2177">
          <Card elevation={0} sx={{
          border: '1px solid #E5E7EB',
          height: '100%',
          borderRadius: '8px'
        }} data-id="element-2178">
            <CardContent data-id="element-2179">
              <Typography variant="subtitle2" color="#6B7280" gutterBottom fontWeight={600} textTransform="uppercase" data-id="element-2180">
                Current Month Forecast
              </Typography>
              <Typography variant="h4" sx={{
              fontWeight: 700,
              color: '#1F2937',
              mb: 1
            }} data-id="element-2181">
                $52.4M
              </Typography>
              <Typography variant="body2" sx={{
              fontWeight: 600,
              color: '#AE2A19'
            }} data-id="element-2182">
                ↓ -2.1% vs target
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>;
};
// --- Tab 3: Day-Wise & YTD ---
const dayWiseData = [{
  partner: 'RIA',
  todayTrans: 4520,
  todayVolUSD: 1250000,
  ytdTrans26: 415000,
  ytdVolUSD26: 115000000,
  ytdVolUSD25: 102000000,
  yoyGrowth: 12.7
}, {
  partner: 'Western Union',
  todayTrans: 6100,
  todayVolUSD: 1850000,
  ytdTrans26: 580000,
  ytdVolUSD26: 165000000,
  ytdVolUSD25: 170000000,
  yoyGrowth: -2.9
}, {
  partner: 'MoneyGram',
  todayTrans: 3800,
  todayVolUSD: 950000,
  ytdTrans26: 320000,
  ytdVolUSD26: 85000000,
  ytdVolUSD25: 76000000,
  yoyGrowth: 11.8
}];
const DayWiseYTDTab = () => {
  const handleExport = (type: string) => {
    downloadCSV(dayWiseData, `day_wise_ytd.${type === 'excel' ? 'xlsx' : type}`);
  };
  return <Box data-id="element-2183">
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2
    }} data-id="element-2184">
        <Typography variant="h6" sx={{
        fontWeight: 600,
        color: '#1F2937'
      }} data-id="element-2185">
          Day-Wise &amp; YTD Performance
        </Typography>
        <ExportMenu onExport={handleExport} filename="day_wise" data-id="element-2186" />
      </Box>
      <TableContainer component={Paper} elevation={0} sx={{
      border: '1px solid #E5E7EB',
      borderRadius: '8px'
    }} data-id="element-2187">
        <Table size="small" data-id="element-2188">
          <TableHead sx={{
          bgcolor: '#F8F9FA'
        }} data-id="element-2189">
            <TableRow data-id="element-2190">
              <TableCell sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2191">
                Partner Name
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2192">
                Today's Trans
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2193">
                Today's Vol (USD)
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2194">
                YTD Trans 2026
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2195">
                YTD Vol (USD) 2026
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2196">
                % YoY Growth
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-id="element-2197">
            {dayWiseData.map(row => <TableRow key={row.partner} hover data-id="element-2198">
                <TableCell sx={{
              fontWeight: 600,
              color: '#1F2937'
            }} data-id="element-2199">
                  {row.partner}
                </TableCell>
                <TableCell align="right" data-id="element-2200">
                  {row.todayTrans.toLocaleString()}
                </TableCell>
                <TableCell align="right" data-id="element-2201">
                  ${row.todayVolUSD.toLocaleString()}
                </TableCell>
                <TableCell align="right" data-id="element-2202">
                  {row.ytdTrans26.toLocaleString()}
                </TableCell>
                <TableCell align="right" data-id="element-2203">
                  ${row.ytdVolUSD26.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{
              color: row.yoyGrowth >= 0 ? '#0B7923' : '#AE2A19',
              fontWeight: 600
            }} data-id="element-2204">
                  {row.yoyGrowth > 0 ? '+' : ''}
                  {row.yoyGrowth.toFixed(1)}%
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>;
};
// --- Tab 4: Month Wise ---
const MonthWiseTab = () => {
  const [partner, setPartner] = useState('RIA');
  const handleExport = (type: string) => {
    downloadCSV([{
      month: 'Jan',
      vol: 1000
    }], `month_wise.${type === 'excel' ? 'xlsx' : type}`);
  };
  return <Box data-id="element-2205">
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2,
      flexWrap: 'wrap',
      gap: 2
    }} data-id="element-2206">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }} data-id="element-2207">
          <Typography variant="h6" sx={{
          fontWeight: 600,
          color: '#1F2937'
        }} data-id="element-2208">
            Month Wise Comparison
          </Typography>
          <FormControl size="small" sx={{
          minWidth: 200
        }} data-id="element-2209">
            <InputLabel data-id="element-2210">Partner</InputLabel>
            <Select value={partner} label="Partner" onChange={e => setPartner(e.target.value)} sx={{
            borderRadius: '6px'
          }} data-id="element-2211">
              {['RIA', 'Western Union', 'MoneyGram'].map(p => <MenuItem key={p} value={p} data-id="element-2212">
                  {p}
                </MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <ExportMenu onExport={handleExport} filename="month_wise" data-id="element-2213" />
      </Box>
      <Paper sx={{
      p: 4,
      textAlign: 'center',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      bgcolor: '#F8F9FA'
    }} elevation={0} data-id="element-2214">
        <Typography color="#6B7280" data-id="element-2215">
          Select a partner to view month-wise historical data.
        </Typography>
      </Paper>
    </Box>;
};
// --- Tab 5: Corridor Strategy ---
const CorridorStrategyTab = () => {
  const handleExport = (type: string) => {
    downloadCSV([{
      corridor: 'GH-UK',
      vol: 1000
    }], `corridor.${type === 'excel' ? 'xlsx' : type}`);
  };
  return <Box data-id="element-2216">
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2
    }} data-id="element-2217">
        <Typography variant="h6" sx={{
        fontWeight: 600,
        color: '#1F2937'
      }} data-id="element-2218">
          Corridor-Wise Strategy View
        </Typography>
        <ExportMenu onExport={handleExport} filename="corridor" data-id="element-2219" />
      </Box>
      <Paper sx={{
      p: 4,
      textAlign: 'center',
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      bgcolor: '#F8F9FA'
    }} elevation={0} data-id="element-2220">
        <Typography color="#6B7280" data-id="element-2221">
          Corridor performance breakdown by partner.
        </Typography>
      </Paper>
    </Box>;
};
// --- Tab 6: Compliance & AML ---
const complianceData = [{
  date: '2026-04-24',
  partner: 'RIA',
  totalTxns: 4520,
  kycPending: 12,
  amlFlags: 3,
  highRisk: 5,
  status: 'Reviewing'
}, {
  date: '2026-04-24',
  partner: 'Western Union',
  totalTxns: 6100,
  kycPending: 8,
  amlFlags: 1,
  highRisk: 2,
  status: 'Cleared'
}, {
  date: '2026-04-24',
  partner: 'MoneyGram',
  totalTxns: 3800,
  kycPending: 24,
  amlFlags: 7,
  highRisk: 12,
  status: 'Action Required'
}, {
  date: '2026-04-23',
  partner: 'RIA',
  totalTxns: 4100,
  kycPending: 0,
  amlFlags: 0,
  highRisk: 1,
  status: 'Cleared'
}, {
  date: '2026-04-23',
  partner: 'Western Union',
  totalTxns: 5900,
  kycPending: 2,
  amlFlags: 0,
  highRisk: 0,
  status: 'Cleared'
}];
const ComplianceTab = () => {
  const handleExport = (type: string) => {
    downloadCSV(complianceData, `compliance_report.${type === 'excel' ? 'xlsx' : type}`);
  };
  const getStatusChip = (status: string) => {
    switch (status) {
      case 'Cleared':
        return <Chip label={status} size="small" sx={{
          bgcolor: '#ECFDF5',
          color: '#0B7923',
          fontWeight: 600
        }} data-id="element-2222" />;
      case 'Reviewing':
        return <Chip label={status} size="small" sx={{
          bgcolor: '#FEF3C7',
          color: '#B38600',
          fontWeight: 600
        }} data-id="element-2223" />;
      case 'Action Required':
        return <Chip label={status} size="small" sx={{
          bgcolor: '#FEE2E2',
          color: '#AE2A19',
          fontWeight: 600
        }} data-id="element-2224" />;
      default:
        return <Chip label={status} size="small" data-id="element-2225" />;
    }
  };
  return <Box data-id="element-2226">
      <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2
    }} data-id="element-2227">
        <Typography variant="h6" sx={{
        fontWeight: 600,
        color: '#1F2937'
      }} data-id="element-2228">
          Compliance & AML Report
        </Typography>
        <ExportMenu onExport={handleExport} filename="compliance" data-id="element-2229" />
      </Box>
      <TableContainer component={Paper} elevation={0} sx={{
      border: '1px solid #E5E7EB',
      borderRadius: '8px'
    }} data-id="element-2230">
        <Table size="small" data-id="element-2231">
          <TableHead sx={{
          bgcolor: '#F8F9FA'
        }} data-id="element-2232">
            <TableRow data-id="element-2233">
              <TableCell sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2234">
                Date
              </TableCell>
              <TableCell sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2235">
                Partner
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2236">
                Total Txns
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2237">
                KYC Pending
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2238">
                AML Flags
              </TableCell>
              <TableCell align="right" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2239">
                High Risk Txns
              </TableCell>
              <TableCell align="center" sx={{
              fontWeight: 600,
              color: '#6B7280'
            }} data-id="element-2240">
                Review Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody data-id="element-2241">
            {complianceData.map((row, idx) => <TableRow key={idx} hover data-id="element-2242">
                <TableCell sx={{
              fontWeight: 500
            }} data-id="element-2243">
                  {row.date}
                </TableCell>
                <TableCell sx={{
              fontWeight: 600,
              color: '#1F2937'
            }} data-id="element-2244">
                  {row.partner}
                </TableCell>
                <TableCell align="right" data-id="element-2245">
                  {row.totalTxns.toLocaleString()}
                </TableCell>
                <TableCell align="right" sx={{
              color: row.kycPending > 0 ? '#B38600' : 'inherit',
              fontWeight: row.kycPending > 0 ? 600 : 400
            }} data-id="element-2246">
                  {row.kycPending}
                </TableCell>
                <TableCell align="right" sx={{
              color: row.amlFlags > 0 ? '#AE2A19' : 'inherit',
              fontWeight: row.amlFlags > 0 ? 600 : 400
            }} data-id="element-2247">
                  {row.amlFlags}
                </TableCell>
                <TableCell align="right" sx={{
              color: row.highRisk > 0 ? '#AE2A19' : 'inherit',
              fontWeight: row.highRisk > 0 ? 600 : 400
            }} data-id="element-2248">
                  {row.highRisk}
                </TableCell>
                <TableCell align="center" data-id="element-2249">
                  {getStatusChip(row.status)}
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>;
};
// --- Tab 7: Custom Report Builder ---
const CustomReportTab = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const handleGenerate = () => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setShowResults(true);
    }, 1000);
  };
  const handleExport = (type: string) => {
    downloadCSV([{
      id: 'TXN123',
      amount: 500
    }], `custom_report.${type === 'excel' ? 'xlsx' : type}`);
  };
  return <Box data-id="element-2250">
      <Typography variant="h6" sx={{
      fontWeight: 600,
      color: '#1F2937',
      mb: 3
    }} data-id="element-2251">
        Custom Report Builder
      </Typography>

      <Paper sx={{
      p: 3,
      mb: 4,
      border: '1px solid #E5E7EB',
      borderRadius: '8px',
      bgcolor: 'white'
    }} elevation={0} data-id="element-2252">
        <Grid container spacing={3} data-id="element-2253">
          <Grid item xs={12} md={3} data-id="element-2254">
            <TextField fullWidth label="From Date" type="date" size="small" InputLabelProps={{
            shrink: true
          }} defaultValue="2026-04-01" sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px'
            }
          }} data-id="element-2255" />
          </Grid>
          <Grid item xs={12} md={3} data-id="element-2256">
            <TextField fullWidth label="To Date" type="date" size="small" InputLabelProps={{
            shrink: true
          }} defaultValue="2026-04-24" sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px'
            }
          }} data-id="element-2257" />
          </Grid>
          <Grid item xs={12} md={3} data-id="element-2258">
            <FormControl fullWidth size="small" data-id="element-2259">
              <InputLabel data-id="element-2260">Region</InputLabel>
              <Select defaultValue="all" label="Region" sx={{
              borderRadius: '6px'
            }} data-id="element-2261">
                <MenuItem value="all" data-id="element-2262">All Regions</MenuItem>
                <MenuItem value="ghana" data-id="element-2263">Ghana</MenuItem>
                <MenuItem value="nigeria" data-id="element-2264">Nigeria</MenuItem>
                <MenuItem value="kenya" data-id="element-2265">Kenya</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3} data-id="element-2266">
            <FormControl fullWidth size="small" data-id="element-2267">
              <InputLabel data-id="element-2268">Transaction Type</InputLabel>
              <Select defaultValue="all" label="Transaction Type" sx={{
              borderRadius: '6px'
            }} data-id="element-2269">
                <MenuItem value="all" data-id="element-2270">All Types</MenuItem>
                <MenuItem value="cash" data-id="element-2271">Cash Pickup</MenuItem>
                <MenuItem value="bank" data-id="element-2272">Bank Transfer</MenuItem>
                <MenuItem value="mobile" data-id="element-2273">Mobile Money</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3} data-id="element-2274">
            <FormControl fullWidth size="small" data-id="element-2275">
              <InputLabel data-id="element-2276">Status</InputLabel>
              <Select defaultValue="all" label="Status" sx={{
              borderRadius: '6px'
            }} data-id="element-2277">
                <MenuItem value="all" data-id="element-2278">All Statuses</MenuItem>
                <MenuItem value="success" data-id="element-2279">Success</MenuItem>
                <MenuItem value="failed" data-id="element-2280">Failed</MenuItem>
                <MenuItem value="pending" data-id="element-2281">Pending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sx={{
          display: 'flex',
          justifyContent: 'flex-end'
        }} data-id="element-2282">
            <Button variant="contained" onClick={handleGenerate} disabled={isGenerating} startIcon={<SearchIcon size={16} data-id="element-2284" />} sx={{
            bgcolor: '#0052CC',
            textTransform: 'none',
            borderRadius: '6px',
            px: 4,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#003DA8',
              boxShadow: 'none'
            }
          }} data-id="element-2283">
              {isGenerating ? 'Generating...' : 'Generate Report'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {showResults && <Box data-id="element-2285">
          <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }} data-id="element-2286">
            <Typography variant="subtitle1" sx={{
          fontWeight: 600,
          color: '#1F2937'
        }} data-id="element-2287">
              Report Results (1,245 records found)
            </Typography>
            <ExportMenu onExport={handleExport} filename="custom_report" data-id="element-2288" />
          </Box>
          <TableContainer component={Paper} elevation={0} sx={{
        border: '1px solid #E5E7EB',
        borderRadius: '8px'
      }} data-id="element-2289">
            <Table size="small" data-id="element-2290">
              <TableHead sx={{
            bgcolor: '#F8F9FA'
          }} data-id="element-2291">
                <TableRow data-id="element-2292">
                  <TableCell sx={{
                fontWeight: 600,
                color: '#6B7280'
              }} data-id="element-2293">
                    Txn ID
                  </TableCell>
                  <TableCell sx={{
                fontWeight: 600,
                color: '#6B7280'
              }} data-id="element-2294">
                    Date
                  </TableCell>
                  <TableCell sx={{
                fontWeight: 600,
                color: '#6B7280'
              }} data-id="element-2295">
                    Region
                  </TableCell>
                  <TableCell sx={{
                fontWeight: 600,
                color: '#6B7280'
              }} data-id="element-2296">
                    Type
                  </TableCell>
                  <TableCell align="right" sx={{
                fontWeight: 600,
                color: '#6B7280'
              }} data-id="element-2297">
                    Amount
                  </TableCell>
                  <TableCell align="center" sx={{
                fontWeight: 600,
                color: '#6B7280'
              }} data-id="element-2298">
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody data-id="element-2299">
                {[{
              id: 'TXN-98234',
              date: '2026-04-24 14:30',
              region: 'Ghana',
              type: 'Mobile Money',
              amount: 'GHS 2,500',
              status: 'Success'
            }, {
              id: 'TXN-98235',
              date: '2026-04-24 14:28',
              region: 'Nigeria',
              type: 'Bank Transfer',
              amount: 'NGN 150,000',
              status: 'Pending'
            }, {
              id: 'TXN-98236',
              date: '2026-04-24 14:15',
              region: 'Kenya',
              type: 'Cash Pickup',
              amount: 'KES 45,000',
              status: 'Failed'
            }, {
              id: 'TXN-98237',
              date: '2026-04-24 13:50',
              region: 'Ghana',
              type: 'Bank Transfer',
              amount: 'GHS 12,000',
              status: 'Success'
            }, {
              id: 'TXN-98238',
              date: '2026-04-24 13:45',
              region: 'Nigeria',
              type: 'Mobile Money',
              amount: 'NGN 25,000',
              status: 'Success'
            }].map((row, idx) => <TableRow key={idx} hover data-id="element-2300">
                    <TableCell sx={{
                fontWeight: 500,
                color: '#0052CC'
              }} data-id="element-2301">
                      {row.id}
                    </TableCell>
                    <TableCell data-id="element-2302">{row.date}</TableCell>
                    <TableCell data-id="element-2303">{row.region}</TableCell>
                    <TableCell data-id="element-2304">{row.type}</TableCell>
                    <TableCell align="right" sx={{
                fontWeight: 600
              }} data-id="element-2305">
                      {row.amount}
                    </TableCell>
                    <TableCell align="center" data-id="element-2306">
                      <Chip label={row.status} size="small" sx={{
                  bgcolor: row.status === 'Success' ? '#ECFDF5' : row.status === 'Failed' ? '#FEE2E2' : '#FEF3C7',
                  color: row.status === 'Success' ? '#0B7923' : row.status === 'Failed' ? '#AE2A19' : '#B38600',
                  fontWeight: 600,
                  fontSize: '0.7rem'
                }} data-id="element-2307" />
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>}
    </Box>;
};
// --- Main Reports Page ---
interface ReportsProps {
  onLogout: () => void;
}
export const Reports: React.FC<ReportsProps> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [globalRegion, setGlobalRegion] = useState('all');
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  return <Box sx={{
    display: 'flex',
    height: '100vh',
    bgcolor: '#F8FAFC'
  }} data-id="element-2308">
      <Sidebar open={mobileOpen} onClose={() => setMobileOpen(false)} activePage="reports" data-id="element-2309" />

      <Box sx={{
      flexGrow: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }} data-id="element-2310">
        <TopBar onMenuClick={() => setMobileOpen(true)} onLogout={onLogout} data-id="element-2311" />

        <Box sx={{
        p: 3,
        flexGrow: 1,
        overflow: 'auto'
      }} data-id="element-2312">
          <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexWrap: 'wrap',
          gap: 2
        }} data-id="element-2313">
            <Typography variant="h4" sx={{
            fontWeight: 700,
            color: '#1F2937',
            fontSize: '2rem'
          }} data-id="element-2314">
              Reports &amp; Analytics
            </Typography>

            <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }} data-id="element-2315">
              <FilterIcon size={20} color="#6B7280" data-id="element-2316" />
              <FormControl size="small" sx={{
              minWidth: 200,
              bgcolor: 'white'
            }} data-id="element-2317">
                <InputLabel data-id="element-2318">Global Region Filter</InputLabel>
                <Select value={globalRegion} label="Global Region Filter" onChange={e => setGlobalRegion(e.target.value)} sx={{
                borderRadius: '6px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#E5E7EB'
                }
              }} data-id="element-2319">
                  <MenuItem value="all" data-id="element-2320">All Regions</MenuItem>
                  <MenuItem value="ghana" data-id="element-2321">Ghana</MenuItem>
                  <MenuItem value="nigeria" data-id="element-2322">Nigeria</MenuItem>
                  <MenuItem value="kenya" data-id="element-2323">Kenya</MenuItem>
                  <MenuItem value="cotedivoire" data-id="element-2324">Cote d'Ivoire</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Paper sx={{
          width: '100%',
          bgcolor: 'white',
          borderRadius: '8px',
          border: '1px solid #E5E7EB',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 600,
          boxShadow: '0px 1px 2px rgba(0,0,0,0.05)'
        }} elevation={0} data-id="element-2325">
            <Box sx={{
            borderBottom: 1,
            borderColor: '#E5E7EB',
            px: 2,
            pt: 2,
            bgcolor: '#F8F9FA',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px'
          }} data-id="element-2326">
              <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" sx={{
              minHeight: 48,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                color: '#6B7280',
                minHeight: 48,
                '&.Mui-selected': {
                  color: '#0052CC'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#0052CC',
                height: 3
              }
            }} data-id="element-2327">
                <Tab label="Daily Monitoring" data-id="element-2328" />
                <Tab label="Financial Flow" data-id="element-2329" />
                <Tab label="Day-Wise & YTD" data-id="element-2330" />
                <Tab label="Month Wise" data-id="element-2331" />
                <Tab label="Corridor Strategy" data-id="element-2332" />
                <Tab label="Compliance & AML" data-id="element-2333" />
                <Tab label="Custom Report Builder" data-id="element-2334" />
              </Tabs>
            </Box>

            <Box sx={{
            px: 3,
            flexGrow: 1
          }} data-id="element-2335">
              <TabPanel value={tabValue} index={0} data-id="element-2336">
                <DailyMonitoringTab data-id="element-2337" />
              </TabPanel>
              <TabPanel value={tabValue} index={1} data-id="element-2338">
                <FinancialFlowTab data-id="element-2339" />
              </TabPanel>
              <TabPanel value={tabValue} index={2} data-id="element-2340">
                <DayWiseYTDTab data-id="element-2341" />
              </TabPanel>
              <TabPanel value={tabValue} index={3} data-id="element-2342">
                <MonthWiseTab data-id="element-2343" />
              </TabPanel>
              <TabPanel value={tabValue} index={4} data-id="element-2344">
                <CorridorStrategyTab data-id="element-2345" />
              </TabPanel>
              <TabPanel value={tabValue} index={5} data-id="element-2346">
                <ComplianceTab data-id="element-2347" />
              </TabPanel>
              <TabPanel value={tabValue} index={6} data-id="element-2348">
                <CustomReportTab data-id="element-2349" />
              </TabPanel>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>;
};
export default Reports;